import React from 'react'
import * as yup from 'yup'
import { Link } from 'react-router-dom'
import isEqual from 'lodash.isequal'
import axios from 'axios'

import FormGroup from '@softwaredev_cegeka/stix-form/dist/components/FormGroup'
import Input from '@softwaredev_cegeka/stix-form/dist/components/Input'
import Textarea from '@softwaredev_cegeka/stix-form/dist/components/Textarea'
import BaseForm from '@softwaredev_cegeka/stix-form/dist/components/BaseForm'
import AxiosSelect from '@softwaredev_cegeka/stix-form-select/dist/components/AxiosSelect'
import Select from '@softwaredev_cegeka/stix-form-select/dist/components/Select'
import CreatableSelect from '@softwaredev_cegeka/stix-form-select/dist/components/CreatableSelect'
import Grid from '@softwaredev_cegeka/stix-core/dist/components/Grid'
import Alert from '@softwaredev_cegeka/stix-core/dist/components/Alert'
import Checkbox from '@softwaredev_cegeka/stix-form/dist/components/Checkbox'
import Icon from '@softwaredev_cegeka/stix-core/dist/components/Icon'
import LinkButton from '@softwaredev_cegeka/stix-core/dist/components/Button/subcomponents/LinkButton'

import FormLayout from './FormLayout'
import FileUpload from './FileUpload'
import Notifier from '../Notifier'
import { authenticatedUserStore, notifierStore, newsStore } from '../../stores'
import { authorize } from '../index'
import RichTextField from '../RichTextField'
import Preview from '../Preview'
import {
    ARTICLE_ATTACHMENT_ALLOWED_TYPES,
    ARTICLE_ATTACHMENT_URL,
    ARTICLE_API_URL,
    ARTICLE_CATEGORIES,
    ARTICLE_COMPANY_ID,
    ARTICLE_IMAGE_MAX_SIZE,
    ARTICLE_SUBCATEGORIES,
    ARTICLE_TYPES,
    AUTHOR_COMPANY,
    CSA_NOTIFICATION_TEMPLATE_URL,
    PRE_POPULATE_EMAIL_MAPPING,
    SECURITY_ADVISORY_TEMPLATE_URL,
    SECURITY_ADVISORY_NEWS,
    SECURITY_ADVISORY_NOTIFICATION,
    SECURITY_VULNERABILITIES,
    SUBCATEGORY_MAP,
} from './constants'

import './articleForm.scss'

const EMAIL_FIELDS = [
    'notification_email_to',
    'notification_email_cc',
    'notification_email_bcc',
]

/**
 * ArticleForm component is used to create or edit articles. It handles form state, validation, and submission.
 * It supports different types of articles, categories, and subcategories, and can pre-populate email fields based on selected category/subcategory.
 */
class ArticleForm extends BaseForm {
    // create instance and add interceptor so auth header is always current
    axiosInstance = axios.create()

    constructor(props) {
        super(props)
        this.isEditTemplateLoadBlocked = !!props.articleId
        this.subcategoryContentCache = {}
        // ensure auth header is always up-to-date for requests
        this.axiosInstance.interceptors.request.use(config => {
            config.headers = config.headers || {}
            config.headers.Authorization = `${authenticatedUserStore.authToken}`
            return config
        })
    }
    getTypes() {
        const options = [
            { value: ARTICLE_TYPES.NEWS, label: ARTICLE_TYPES.NEWS },
            {
                value: ARTICLE_TYPES.KNOWLEDGE,
                label: ARTICLE_TYPES.KNOWLEDGE,
            },
        ]
        if (authenticatedUserStore.isAllowed('WRITE_INCIDENT')) {
            options.push({
                value: ARTICLE_TYPES.INCIDENT,
                label: ARTICLE_TYPES.INCIDENT,
            })
        }

        options.sort((a, b) => a.label.localeCompare(b.label))

        return options
    }

    getCategories() {
        const selectedType = this.getValue('type')?.value

        const categoryMap = new Map(
            Object.entries({
                [ARTICLE_TYPES.KNOWLEDGE]: [ARTICLE_SUBCATEGORIES.GENERAL],
                [ARTICLE_TYPES.INCIDENT]: [
                    ARTICLE_SUBCATEGORIES.GENERAL,
                    ARTICLE_CATEGORIES.INCIDENT,
                ],
                [ARTICLE_TYPES.NEWS]: [
                    ARTICLE_CATEGORIES.COMMUNICATIONS,
                    ARTICLE_SUBCATEGORIES.GENERAL,
                    ARTICLE_CATEGORIES.KUBEPORT,
                    ARTICLE_CATEGORIES.USD_ANNOUNCEMENTS,
                    ARTICLE_CATEGORIES.SECURITY,
                    ...(authenticatedUserStore.isAllowed(
                        'CREATE_SECURITY_ARTICLE'
                    ) ||
                    authenticatedUserStore.isAllowed('CSA_NOTIFICATION_CRUD')
                        ? [SECURITY_VULNERABILITIES]
                        : []),
                ],
            })
        )

        const categoryList = categoryMap.has(selectedType)
            ? categoryMap.get(selectedType)
            : []

        return categoryList
            .map(item => ({ value: item, label: item }))
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    getSubcategories() {
        const subCategoryKey = `${this.getValue('type')?.value}-${this.getValue('category')?.value}`

        const subcategoryList = Object.prototype.hasOwnProperty.call(
            SUBCATEGORY_MAP,
            subCategoryKey
        )
            ? SUBCATEGORY_MAP[subCategoryKey]
            : []

        return subcategoryList
            .map(item => ({ value: item, label: item }))
            .filter(
                s =>
                    !(
                        s.value === SECURITY_ADVISORY_NOTIFICATION &&
                        !authenticatedUserStore.isAllowed(
                            'CSA_NOTIFICATION_CRUD'
                        )
                    )
            )
            .sort((a, b) => a.label.localeCompare(b.label))
    }

    static initialValues = {
        company_id: ARTICLE_COMPANY_ID,
        author_first_name: '',
        author_last_name: '',
        category: {
            value: ARTICLE_CATEGORIES.COMMUNICATIONS,
            label: ARTICLE_CATEGORIES.COMMUNICATIONS,
        },
        subcategory: {
            value: ARTICLE_SUBCATEGORIES.GENERAL,
            label: ARTICLE_SUBCATEGORIES.GENERAL,
        },
        target_audience: [],
        tags: [],
        content: '',
        title: '',
        type: {
            value: ARTICLE_TYPES.NEWS,
            label: ARTICLE_TYPES.NEWS,
        },
        visible: false,
        image_data: '',
        short_summary: '',
        attachment: '',
        attachmentLink: '',
        attachmentFilename: '',
        notification_email_to: [],
        notification_email_cc: [],
        notification_email_bcc: [],
    }

    pages = [
        {
            title: 'article-form',
            renderMethod: this.renderPage,
            validationSchema: yup.object().shape({
                author_first_name: yup.string().required(),
                author_last_name: yup.string().required(),
                type: yup
                    .object()
                    .shape({
                        label: yup
                            .string()
                            .oneOf(this.getTypes().map(o => o.label)),
                        value: yup
                            .string()
                            .oneOf(this.getTypes().map(o => o.value)),
                    })
                    .required(),
                category: yup
                    .object()
                    .shape({
                        label: yup.string(),
                        value: yup.string(),
                    })
                    .required(),
                subcategory: yup
                    .object()
                    .shape({
                        label: yup.string(),
                        value: yup.string(),
                    })
                    .required(),
                title: yup.string().required(),
                notification_email_to: yup
                    .array()
                    .of(
                        yup
                            .string()
                            .email('Please provide a valid email address')
                    )
                    .test({
                        name: 'to-required-if-cc-or-bcc',
                        message:
                            'Email Notification TO is required if CC or BCC is filled',
                        test: function (value) {
                            const cc = this.parent?.notification_email_cc || []
                            const bcc =
                                this.parent?.notification_email_bcc || []
                            // If both CC and BCC are empty, TO is optional
                            if (cc.length === 0 && bcc.length === 0) {
                                return true
                            }
                            // If CC or BCC has values, TO must have at least one email
                            return value && value.length > 0
                        },
                    }),
                notification_email_cc: yup
                    .array()
                    .of(
                        yup
                            .string()
                            .email('Please provide a valid email address')
                    )
                    .default([]),
                notification_email_bcc: yup
                    .array()
                    .of(
                        yup
                            .string()
                            .email('Please provide a valid email address')
                    )
                    .default([]),
                short_summary: yup.string().required(),
                content: yup.string().min(2).required(),
                target_audience: yup
                    .array()
                    .of(
                        yup.object().shape({
                            label: yup.string(),
                            value: yup.string(),
                        })
                    )
                    .nullable(),
                tags: yup
                    .array()
                    .of(
                        yup.object().shape({
                            label: yup.string(),
                            value: yup.string(),
                        })
                    )
                    .nullable(),
            }),
        },
    ]

    async componentDidMount() {
        const { articleId } = this.props

        if (articleId) {
            try {
                await newsStore.loadNewsItem(articleId)
                let attachmentLink = ''
                let attachmentFilename = ''
                try {
                    const attachmentResponse = await axios.get(
                        ARTICLE_ATTACHMENT_URL(articleId),
                        { responseType: 'blob' }
                    )

                    const contentDisposition =
                        attachmentResponse.headers['content-disposition']
                    attachmentFilename = 'attachment.pdf'
                    if (contentDisposition) {
                        const filenameMatch =
                            contentDisposition.match(/filename="(.+)"/)
                        if (filenameMatch && filenameMatch[1]) {
                            attachmentFilename = filenameMatch[1]
                        }
                    }

                    attachmentLink = URL.createObjectURL(
                        attachmentResponse.data
                    )
                } catch (err) {
                    console.error(err)
                }
                this.setState({
                    values: {
                        ...newsStore.newsDetail,
                        notification_email_to:
                            newsStore.newsDetail.notification_email_to || [],
                        notification_email_cc:
                            newsStore.newsDetail.notification_email_cc || [],
                        notification_email_bcc:
                            newsStore.newsDetail.notification_email_bcc || [],
                        company_id: ARTICLE_COMPANY_ID,
                        target_audience: newsStore.newsDetail.organizations.map(
                            organization => ({
                                label: organization.name,
                                value: organization.organization_id,
                            })
                        ),
                        category: {
                            label: newsStore.newsDetail.category,
                            value: newsStore.newsDetail.category,
                        },
                        subcategory: {
                            label: newsStore.newsDetail.subcategory,
                            value: newsStore.newsDetail.subcategory,
                        },
                        type: {
                            value: newsStore.newsDetail.type,
                            label: newsStore.newsDetail.type,
                        },
                        tags: newsStore.newsDetail.tags.map(tag => ({
                            label: tag,
                            value: tag,
                        })),
                    },
                    attachmentLink,
                    attachmentFilename,
                })
            } catch (err) {
                this.setState({
                    notifierVariant: 'error',
                    notifierTitle: 'Missing article.',
                })
                notifierStore.setShowMessage(true)
            }
        } else {
            if (authenticatedUserStore.userData) {
                this.setState({
                    values: {
                        ...this.state.values,
                        author_first_name:
                            authenticatedUserStore.userData.firstName,
                        author_last_name:
                            authenticatedUserStore.userData.lastName,
                    },
                    notifierTitle: 'Your request was successful',
                    notifierVariant: 'success',
                })
            }
        }
    }

    setContentValue(prevState) {
        const previousSubcategory = prevState.values.subcategory?.value
        const currentSubcategory = this.state.values.subcategory?.value
        const previousCategory = prevState.values.category?.value
        const currentCategory = this.state.values.category?.value

        if (previousSubcategory === currentSubcategory) {
            return
        }

        if (
            previousSubcategory &&
            previousCategory === SECURITY_VULNERABILITIES &&
            prevState.values.content !== undefined
        ) {
            this.subcategoryContentCache[previousSubcategory] =
                prevState.values.content
        }

        const templateBySubcategory = {
            [SECURITY_ADVISORY_NEWS]: SECURITY_ADVISORY_TEMPLATE_URL,
            [SECURITY_ADVISORY_NOTIFICATION]: CSA_NOTIFICATION_TEMPLATE_URL,
        }
        const endpoint = templateBySubcategory[currentSubcategory]

        if (!endpoint) {
            if (
                [
                    SECURITY_ADVISORY_NEWS,
                    SECURITY_ADVISORY_NOTIFICATION,
                ].includes(previousSubcategory) &&
                previousCategory === SECURITY_VULNERABILITIES &&
                currentCategory !== SECURITY_VULNERABILITIES
            ) {
                this.setValue('content', '')
            }
            return
        }

        if (currentCategory !== SECURITY_VULNERABILITIES) {
            return
        }

        if (
            Object.prototype.hasOwnProperty.call(
                this.subcategoryContentCache,
                currentSubcategory
            )
        ) {
            this.setValue(
                'content',
                this.subcategoryContentCache[currentSubcategory]
            )
            return
        }

        this.axiosInstance
            .get(endpoint)
            .then(response => {
                const templateContent = this.cleanMarkdown(response.data)
                this.subcategoryContentCache[currentSubcategory] =
                    templateContent
                this.setValue('content', templateContent)
            })
            .catch(error => {
                console.error('Error fetching template:', error)
            })
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            !isEqual(prevState.values.image_data, this.state.values.image_data)
        ) {
            this.generateImagePreview()
        }

        // Pre-populates email fields for new articles when category/subcategory changes
        // Only applies to new articles to avoid overwriting edited article data
        if (!this.props.articleId) {
            const currentKey = `${this.state.values.category?.value}|${this.state.values.subcategory?.value}`
            const prevKey = `${prevState.values.category?.value}|${prevState.values.subcategory?.value}`

            if (currentKey !== prevKey) {
                const emailDefaults = PRE_POPULATE_EMAIL_MAPPING[currentKey]

                this.setState({
                    values: {
                        ...this.state.values,
                        notification_email_to: emailDefaults
                            ? emailDefaults.to
                            : [],
                        notification_email_cc: emailDefaults
                            ? emailDefaults.cc
                            : [],
                        notification_email_bcc: emailDefaults
                            ? emailDefaults.bcc
                            : [],
                    },
                })
            }
        }
        const updateSaveButtonLabel =
            !isEqual(prevState.values.visible, this.state.values.visible) ||
            !isEqual(
                prevState.values.notification_email_to,
                this.state.values.notification_email_to
            )

        if (this.props.updateSaveAndNotify && updateSaveButtonLabel) {
            const hasNotificationRecipients =
                this.state.values.notification_email_to?.length &&
                !this.state.values.sent_notification
            const shouldShowNewsletterAction =
                this.shouldShowNotificationFields() && hasNotificationRecipients

            this.props.updateSaveAndNotify(
                this.state.values.visible && hasNotificationRecipients
                    ? true
                    : shouldShowNewsletterAction
            )
        }

        if (this.isEditTemplateLoadBlocked) {
            this.isEditTemplateLoadBlocked = false
            return
        }

        // Template loading is triggered only by user-driven subcategory changes.
        if (
            prevState.values.subcategory?.value !==
            this.state.values.subcategory?.value
        ) {
            this.setContentValue(prevState)
        }
    }
    // Removes all spaces from the first line of a markdown string
    // We get ghost spaces from the api in front of some rules.
    cleanMarkdown(markdown) {
        if (!markdown) {
            return ''
        }
        const lines = markdown.split('\n')
        if (lines.length === 0) {
            return markdown
        }
        const convertedLines = lines.map(line => line.trim())

        return convertedLines.join('\n')
    }
    generatePayload = async values => {
        let data = new FormData()

        data.append('company_id', values.company_id)
        data.append('author_first_name', values.author_first_name)
        data.append('author_last_name', values.author_last_name)
        data.append('category', values.category?.value)
        data.append('subcategory', values.subcategory?.value)
        data.append('title', values.title)
        data.append('content', values.content) // This is now markdown
        data.append('template', values.content) // Send markdown as template
        data.append('visible', values.visible)
        data.append('type', values.type.value)
        data.append('short_summary', values.short_summary)

        // Handle email fields
        EMAIL_FIELDS.forEach(field => {
            const emails = values[field] || []
            data.append(field, emails.length > 0 ? emails.join(',') : '')
        })

        if (values.image_data && Array.isArray(values.image_data)) {
            data.append(
                'image_data',
                values.image_data ? values.image_data[0] : null
            )
        }
        if (values.attachment && Array.isArray(values.attachment)) {
            data.append(
                'attachment',
                values.attachment ? values.attachment[0] : null
            )
        }
        // target audience
        if (values.target_audience?.length) {
            data.append(
                'target_audience',
                values.target_audience.map(audience => audience.value).join(',')
            )
        } else {
            data.append('target_audience', '')
        }

        //tags
        if (values.tags?.length) {
            data.append('tags', values.tags.map(tag => tag.value).join(','))
        } else {
            data.append('tags', '')
        }

        return data
    }

    submitData = async payload => {
        try {
            let url = ARTICLE_API_URL
            let method = 'POST'
            if (this.props.articleId) {
                url = `${ARTICLE_API_URL}/${this.props.articleId}`
                method = 'PUT'
            }
            let response = await axios({
                url: url,
                headers: {
                    Authorization: `${authenticatedUserStore.authToken}`,
                },
                method: method,
                data: payload,
            })
            if (response.status === 200) {
                notifierStore.setShowMessage(true)
                window.location = '/'
            }
        } catch (err) {
            console.error(err)
            this.setState({
                notifierVariant: 'error',
                notifierTitle: 'Your request failed.',
            })
            notifierStore.setShowMessage(true)
        }
    }

    generateImagePreview() {
        const imageData = this.getValue('image_data')
        if (imageData && imageData[0] && !this.state.imageLoading) {
            if (typeof imageData === 'string') {
                const imageDataDecoded = atob(imageData)
                const isJpg = imageDataDecoded.indexOf('JFIF') === 6
                const isPng = imageDataDecoded.indexOf('PNG') === 1
                const header = isPng
                    ? 'data:image/png;base64, '
                    : isJpg
                      ? 'data:image/jpg;base64, '
                      : ''
                this.setState({
                    // data:image/png;base64,
                    imagePreviewData: `${header}${imageData}`,
                    imageLoading: false,
                })
            } else {
                const reader = new FileReader()
                reader.onload = e => {
                    this.setState({
                        imagePreviewData: e.target.result,
                        imageLoading: false,
                    })
                }
                reader.onerror = e => {
                    this.setState({
                        imagePreviewData: null,
                        imageLoading: false,
                    })
                }

                this.setState({
                    imageLoading: true,
                })
                reader.readAsDataURL(imageData[0])
            }
        }
    }

    removeAttachment() {
        this.setState(prevState => ({
            values: {
                ...prevState.values,
                attachment: null,
            },
            attachmentLink: '',
            attachmentFilename: '',
        }))
    }

    renderImagePreview() {
        return (
            <>
                <span className="stix-form-label">Image preview</span>
                <div className="image-preview-container">
                    <img
                        className="image-preview"
                        alt="Preview of selected file"
                        src={this.state.imagePreviewData}
                    />
                </div>
            </>
        )
    }

    getNewOptionData(inputValue, optionLabel) {
        return {
            isNewOption: true,
            value: inputValue,
            label: optionLabel,
        }
    }

    formatCreateLabel(inputValue) {
        return `${inputValue} (new)`
    }

    // Handlers extracted for readability and reuse
    handleTypeChange = value => {
        this.setState({
            values: {
                ...this.state.values,
                type: value,
                category: { value: '' },
                subcategory: { value: '' },
            },
        })
    }

    handleCategoryChange = value => {
        this.setState({
            values: {
                ...this.state.values,
                category: value,
                subcategory: { value: '' },
            },
        })
    }

    handleSubcategoryChange = value => {
        this.setState({
            values: {
                ...this.state.values,
                subcategory: value,
            },
        })
    }

    handleContentChange = markdown => this.setValue('content', markdown)

    handleCreatableEmailChange = (fieldName, value) => {
        const emails = value ? value.map(v => v.value) : []
        this.setState({
            values: {
                ...this.state.values,
                [fieldName]: emails,
            },
        })
    }

    shouldShowNotificationFields() {
        return (
            this.getValue('category')?.value === SECURITY_VULNERABILITIES &&
            [SECURITY_ADVISORY_NEWS, SECURITY_ADVISORY_NOTIFICATION].includes(
                this.getValue('subcategory')?.value
            )
        )
    }

    // Renders reusable email input with CreatableSelect, hiding dropdown for manual entry
    renderEmailSelect({ label, fieldName }) {
        return (
            <Grid.Col md={12}>
                <FormGroup>
                    <CreatableSelect
                        {...this.getCommonSelectProps(fieldName)}
                        options={[]}
                        label={label}
                        isMulti
                        isClearable
                        formatCreateLabel={this.formatCreateLabel}
                        getNewOptionData={this.getNewOptionData}
                        onChange={value =>
                            this.handleCreatableEmailChange(fieldName, value)
                        }
                        value={this.state.values[fieldName].map(email => ({
                            label: email,
                            value: email,
                        }))}
                        placeholder={'Enter email and press enter...'}
                        error={this.state.errors?.[fieldName]}
                        components={{ Menu: () => null }} // Hide the dropdown
                    />
                </FormGroup>
            </Grid.Col>
        )
    }

    renderPage() {
        const { attachmentLink, attachmentFilename } = this.state

        return (
            <FormLayout className={'article-form'}>
                <Notifier>
                    <Notifier.Toast
                        variant={this.state.notifierVariant}
                        title={this.state.notifierTitle}
                    >
                        You can go to the <Link to={'/'}>All articles</Link>{' '}
                        page for more info.
                    </Notifier.Toast>
                </Notifier>
                <Grid.Row>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <Input
                                {...this.getCommonInputProps(
                                    'author_first_name'
                                )}
                                label="Author First Name"
                                required
                            />
                        </FormGroup>
                    </Grid.Col>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <Input
                                {...this.getCommonInputProps(
                                    'author_last_name'
                                )}
                                label="Author Last Name"
                                required
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <Input
                                value={AUTHOR_COMPANY}
                                label="Author Company"
                                readOnly
                            />
                        </FormGroup>
                    </Grid.Col>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <AxiosSelect
                                {...this.getCommonSelectProps(
                                    'target_audience'
                                )}
                                //TODO FIXME: install the sso-middleware package in this project (remove project specific one) and remove the axios instance.
                                // This will solve the issue of the auth token not being passed.
                                apiClient={this.axiosInstance}
                                apiConfig={{
                                    url: `/api/auth/users/${authenticatedUserStore.username}/tenants`,
                                    params: () => ({}),
                                }}
                                label="Target Audience"
                                itemRenderer={response =>
                                    response.data.tenants.map(tenant => ({
                                        label: tenant.name,
                                        value: tenant.organization_id,
                                    }))
                                }
                                disabled={!this.state.values.company_id}
                                isMulti
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <Select
                                {...this.getCommonSelectProps('type')}
                                options={this.getTypes()}
                                isSearchable
                                required
                                onChange={this.handleTypeChange}
                                label="Type"
                            />
                        </FormGroup>
                    </Grid.Col>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <AxiosSelect
                                {...this.getCommonSelectProps('tags')}
                                getNewOptionData={this.getNewOptionData}
                                formatCreateLabel={this.formatCreateLabel}
                                label="Tags"
                                //TODO FIXME: install the sso-middleware package in this project (remove project specific one) and remove the axios instance.
                                // This will solve the issue of the auth token not being passed.
                                apiClient={this.axiosInstance}
                                apiConfig={{
                                    url: `/api/news/tags`,
                                    params: inputValue => ({
                                        value_like: inputValue,
                                        sort: '+value',
                                        noCache: true,
                                    }),
                                }}
                                itemRenderer={response =>
                                    response.data.results.map(tag => ({
                                        label: tag.value,
                                        value: tag.value,
                                    }))
                                }
                                search
                                isMulti
                                SelectComponent={CreatableSelect}
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <Select
                                {...this.getCommonInputProps('category')}
                                options={this.getCategories()}
                                label="Category"
                                onChange={this.handleCategoryChange}
                                required
                            />
                        </FormGroup>
                    </Grid.Col>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <Select
                                {...this.getCommonInputProps('subcategory')}
                                options={this.getSubcategories()}
                                onChange={this.handleSubcategoryChange}
                                label="Subcategory"
                                required
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col md={12}>
                        <FormGroup>
                            <Input
                                {...this.getCommonInputProps('title')}
                                label="Title"
                                required
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col md={12}>
                        <FormGroup>
                            <Textarea
                                {...this.getCommonInputProps('short_summary')}
                                label="Short summary"
                                required
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col md={12}>
                        <FormGroup>
                            <RichTextField
                                {...this.getCommonInputProps('content')}
                                label="[Markdown] Content"
                                content={this.getValue('content')}
                                onMarkdownChange={this.handleContentChange}
                                required
                                error={this.state.errors?.content}
                            />
                            {this.getValue('category')?.value ===
                                SECURITY_VULNERABILITIES &&
                                [
                                    SECURITY_ADVISORY_NEWS,
                                    SECURITY_ADVISORY_NOTIFICATION,
                                ].includes(
                                    this.getValue('subcategory')?.value
                                ) &&
                                this.getValue('content').length > 0 && (
                                    <Preview
                                        content={this.getValue('content')}
                                        title={this.getValue('title')}
                                        subcategory={
                                            this.getValue('subcategory')?.value
                                        }
                                    />
                                )}
                        </FormGroup>
                        <FormGroup>
                            <Alert variant="secondary">
                                Please note that news articles rendered in
                                Horizon may look slightly different than the
                                preview shown here, due to HTML sanitization.
                            </Alert>
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col md={6}>
                        <FormGroup>
                            {this.props.allowUploads ? (
                                <FileUpload
                                    {...this.getCommonFileUploadProps(
                                        'image_data'
                                    )}
                                    disabled={this.state.imageLoading}
                                    maxSize={16777215}
                                    label={
                                        this.getValue('image_data')
                                            ? 'Change image'
                                            : 'Add image'
                                    }
                                />
                            ) : null}
                        </FormGroup>
                    </Grid.Col>
                    <Grid.Col md={6}>
                        <FormGroup>
                            {this.getValue('image_data') &&
                            !this.state.imageLoading
                                ? this.renderImagePreview()
                                : null}
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Col md={attachmentLink ? 12 : 6}>
                        {attachmentLink ? (
                            <div className={'attachment'}>
                                <span>Attachment</span>
                                <a
                                    href={attachmentLink}
                                    download={attachmentFilename}
                                >
                                    {attachmentFilename}
                                </a>
                                <LinkButton
                                    onClick={() => this.removeAttachment()}
                                >
                                    <Icon icon="delete" />
                                </LinkButton>
                            </div>
                        ) : (
                            <FormGroup>
                                {this.props.allowUploads ? (
                                    <FileUpload
                                        {...this.getCommonFileUploadProps(
                                            'attachment'
                                        )}
                                        maxSize={ARTICLE_IMAGE_MAX_SIZE}
                                        allowedTypes={
                                            ARTICLE_ATTACHMENT_ALLOWED_TYPES
                                        }
                                        label={'Add attachment'}
                                    />
                                ) : null}
                            </FormGroup>
                        )}
                    </Grid.Col>
                </Grid.Row>
                {this.shouldShowNotificationFields() && (
                    <Grid.Row>
                        {this.renderEmailSelect({
                            label: 'Email Notification TO',
                            fieldName: 'notification_email_to',
                        })}
                        {this.renderEmailSelect({
                            label: 'Email Notification CC',
                            fieldName: 'notification_email_cc',
                        })}
                        {this.renderEmailSelect({
                            label: 'Email Notification BCC',
                            fieldName: 'notification_email_bcc',
                        })}
                    </Grid.Row>
                )}
                <Grid.Row>
                    <Grid.Col md={6}>
                        <FormGroup>
                            <Checkbox
                                {...this.getCommonCheckboxProps('visible')}
                                custom
                                label="Make article visible"
                            />
                        </FormGroup>
                    </Grid.Col>
                </Grid.Row>
            </FormLayout>
        )
    }
}

export default authorize(ArticleForm, 'WRITE')
